/**
 * Which STYLE a surface renders in, for the channel it is being served on.
 *
 * ## Style is per-CHANNEL, not per-surface, and that is the whole point
 *
 * The same Drop is PAGE style in the Drop-In directory and can be HUB style on
 * its own domain — `surfaceFacets.ts` says so directly: *"style belongs to the
 * channel. The same Drop is PAGE in the directory and HUB on its own domain, so
 * asking 'what chrome does this surface use' is only meaningful once you say
 * where."* So this takes a channel and answers for that channel, rather than
 * reading one field off the surface.
 *
 * ## Not to be confused with CHROME
 *
 * `DropPageRouter` decides chrome — whether the DevourPlay wrapper is on — from
 * the channel too, and deliberately never reads style. Conflating them shipped
 * once already: choosing the hub layout silently stripped the navbar off a
 * public page. Style is the LAYOUT of the content; chrome is the frame around
 * it. Two questions, same input, different answers.
 *
 * ## The default is PAGE and that is a safety property
 *
 * All 13,681 production Drops carry no `distributions[]`, so every one of them
 * resolves to PAGE and renders exactly as it does today. Nothing changes until
 * a surface is deliberately given a distribution — which is the same fallback
 * discipline `surfaceFacets.ts` applies to visibility.
 */

import type {DistributionChannel, DistributionStyle} from "@devour/client";

/*
 * THE VALUES ARE STRING LITERALS, NOT THE CLIENT'S ENUM OBJECTS — deliberately.
 *
 * openapi-generator 7.x emits enums as `export const X = {...}`, i.e. RUNTIME
 * values, and Vite resolves `@devour/client` through its CommonJS interop:
 *
 *   const DistributionStyle = __vite__cjsImport__devour_client["DistributionStyle"];
 *
 * The named exports do not survive that conversion, so `DistributionStyle` is
 * empty and `DistributionStyle.HUB` is `undefined`. A comparison against it is
 * then SILENTLY false — no error, no warning, the surface just renders the
 * wrong structure. That is exactly what happened: a Drop configured for HUB
 * rendered the page tree because `style === DistributionStyle.HUB` compared a
 * real "HUB" against `undefined`.
 *
 * `vite.config.ts` documents the same hazard for the production build. The
 * types are still imported (erased at build time, so they cost nothing and
 * keep this honest against the schema); only the VALUES are literals.
 */
const STYLE = {PAGE: "PAGE", HUB: "HUB"} as const;
const CHANNEL = {
    DIRECTORY: "DIRECTORY",
    PLATFORM_URL: "PLATFORM_URL",
    CUSTOM_DOMAIN: "CUSTOM_DOMAIN",
} as const;

/** The distribution shape this needs — a subset of the generated `Distribution`. */
interface StyleBearingDistribution {
    channel: DistributionChannel;
    style: DistributionStyle;
    /** Defaults true. `false` stages a row without making it live — see `Distribution.enabled`. */
    enabled?: boolean;
}

export const SURFACE_STYLE = STYLE;
export const SURFACE_CHANNEL = CHANNEL;

/**
 * Resolve the style for one channel.
 *
 * @param distributions the surface's `distributions[]`, or nothing
 * @param channel       where it is being served right now
 */
export function resolveStyle(
    distributions: readonly StyleBearingDistribution[] | null | undefined,
    channel: DistributionChannel,
): DistributionStyle {
    /*
     * `enabled: false` means STAGED, not live — a row a Drop-In Portal
     * operator configured (a layout picked, a hostname typed) but hasn't
     * turned on. It must resolve exactly as though the row were absent,
     * which is what falling through to the PAGE default already does for a
     * channel with no row at all.
     */
    const match = distributions?.find(d => d.channel === channel && d.enabled !== false);
    return match?.style ?? (STYLE.PAGE as DistributionStyle);
}

/**
 * The channel a surface is currently being served on.
 *
 * A custom hostname means CUSTOM_DOMAIN regardless of listing. On the
 * platform's own host, DIRECTORY and PLATFORM_URL are the SAME address —
 * `devourplay.gg/drop-in/:slug` either way — so the address alone cannot
 * tell them apart. `isPublic` is the caller's answer to "is this Drop
 * currently listed in the directory", which is exactly `CHANNEL_DESCRIPTIONS`'
 * definition of DIRECTORY.
 *
 * DIRECTORY was previously unreachable: this always returned PLATFORM_URL on
 * the platform host, so a DIRECTORY row an operator configured in admin —
 * `DistributionsEditor.tsx` offers it — matched no channel `resolveStyle`
 * ever asked for and its `style` had no effect, silently. `isPublic` is the
 * same signal `DropPageRouter` already computes for chrome; this is that
 * question asked a second time, not a new one.
 */
export function channelForHost(
    hostname: string,
    platformHosts: readonly string[],
    isPublic: boolean,
): DistributionChannel {
    const isPlatform = platformHosts.some(h => hostname === h || hostname.endsWith(`.${h}`));
    if (!isPlatform) return CHANNEL.CUSTOM_DOMAIN as DistributionChannel;
    return (isPublic ? CHANNEL.DIRECTORY : CHANNEL.PLATFORM_URL) as DistributionChannel;
}

/**
 * Whether a Drop is public — listed in the directory, wrapped in the
 * DevourPlay app shell.
 *
 * ONE COMPUTATION, because it was two. `DropPageRouter.tsx` computed this
 * distribution-aware (checking the DIRECTORY row's `enabled` flag) while
 * `DropPage.tsx` kept reading the legacy `visibility` field alone — so for
 * any Drop where those two answers diverge (exactly the case
 * `DistributionsEditor.tsx` exists to let an operator create), the chrome
 * decision and the style/layout decision could be computed against two
 * different channels on the SAME page load. Both call sites now use this.
 */
export function isPublicDrop(drop: { distributions?: readonly StyleBearingDistribution[] | null; visibility?: string } | null | undefined): boolean {
    /*
     * `CHANNEL.DIRECTORY`, not an imported `DistributionChannel.DIRECTORY` —
     * this replaced the second call site that compared against the CLIENT'S
     * runtime enum object directly, which is the exact hazard documented at
     * `channelForHost` above: it round-trips fine under the dev server's ESM
     * transform, silently compares against `undefined` under a production
     * Rollup build. Routing through the local string literal here closes it.
     */
    if (!drop) return false;
    /*
     * PRIVATE ALWAYS WINS — an unconditional override, not another branch of
     * the same check. `distributions[]` is additive information about HOW a
     * public surface is served; it must never be a second path to making a
     * PRIVATE one read as public again. Found live 2026-08-24: a Drop set
     * `visibility: PRIVATE` still rendered wrapped in the DevourPlay chrome
     * because its `distributions[]` carried a DIRECTORY row nobody disabled
     * after an unrelated round of testing. An operator who explicitly chose
     * PRIVATE must not have that choice silently reversed by a row they never
     * looked at again — the risk is asymmetric: wrongly-public is a real
     * exposure, wrongly-private is just a display inconvenience.
     */
    if (drop.visibility === "PRIVATE") return false;
    return drop.distributions?.length
        ? drop.distributions.some(d => d.channel === CHANNEL.DIRECTORY && d.enabled !== false)
        : drop.visibility === "PUBLIC";
}
